import { ApplicationConfig, provideZoneChangeDetection, APP_INITIALIZER, LOCALE_ID, inject } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideIonicAngular } from '@ionic/angular/standalone';
import localeEsCO from '@angular/common/locales/es-CO';

import { routes } from './app.routes';
import { authInterceptor } from './core/auth/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { AuthService } from './core/auth/auth.service';
import { PushService } from './core/services/push.service';
import { AppLifecycleService } from './core/services/app-lifecycle.service';
import { SafeAreaService } from './core/services/safe-area.service';
import { PersistentStorageService } from './core/services/persistent-storage.service';
import { VersionCheckService } from './core/services/version-check.service';
import { TenantDateService } from './shared/services/tenant-date.service';

registerLocaleData(localeEsCO);

/**
 * Hydrate the persistent storage mirror BEFORE anything reads critical state
 * synchronously (route guards, execution draft). Must run first (Req 15.2).
 */
function initializePersistentStorage(): () => Promise<void> {
  const storage = inject(PersistentStorageService);
  return () => storage.hydrate();
}

/**
 * Initialize auth state from secure storage on app startup.
 * Amplify v6 uses Keychain (iOS) / Keystore (Android) natively.
 */
function initializeAuth(): () => Promise<void> {
  const authService = inject(AuthService);
  return () => authService.initializeFromStorage();
}

/**
 * Initialize tenant date service from cached config on app startup.
 * Ensures timezone-aware formatting is available from first render.
 */
function initializeTenantDate(): () => Promise<void> {
  const tenantDate = inject(TenantDateService);
  return () => tenantDate.initialize();
}

/**
 * Initialize push notifications if user is already authenticated on app restart.
 * Runs after auth initializer (APP_INITIALIZER order is preserved).
 * On native platforms, registers FCM token and sets up notification listeners.
 */
function initializePush(): () => Promise<void> {
  const authService = inject(AuthService);
  const pushService = inject(PushService);
  return async () => {
    // Cold start: only re-register if the push permission was already granted.
    // Never prompt for the permission here — that must happen in context after
    // an explicit login (see login.page / change-password.page). On iOS the
    // native dialog shows only once, so prompting on cold start risks a hard
    // denial the app cannot recover from (Req 4.1b, ASRG 5.1.1).
    if (authService.isAuthenticated()) {
      await pushService.resumeIfRegistered();
    }
  };
}

/**
 * Wire app foreground/background transitions to GPS + sync-queue lifecycle
 * (Req 6.8). Suspends GPS capture and pauses the sync drain in background;
 * resumes both on foreground. Prevents hung timers / battery drain on iOS.
 */
function initializeAppLifecycle(): () => Promise<void> {
  const lifecycle = inject(AppLifecycleService);
  return () => lifecycle.initialize();
}

/**
 * Publish the device safe-area insets as CSS variables at startup so screens with
 * a fixed bottom bar are laid out correctly on their FIRST paint. On iOS,
 * `env(safe-area-inset-bottom)` resolves to 0 for the first frame of a new fixed
 * element, which placed the bottom action button inside the home-indicator strip
 * until something forced a relayout.
 */
function initializeSafeArea(): () => void {
  const safeArea = inject(SafeAreaService);
  return () => safeArea.initialize();
}

/**
 * On the WEB build only, force open tabs to reload after a deploy by watching
 * version.json. No-op on native (updated via the app stores). Non-blocking:
 * start() never throws, so a failed fetch cannot stall bootstrap.
 */
function initializeVersionCheck(): () => Promise<void> {
  const versionCheck = inject(VersionCheckService);
  return () => versionCheck.start();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    { provide: LOCALE_ID, useValue: 'es-CO' },
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor])),
    provideAnimationsAsync(),
    provideIonicAngular({
      mode: 'md',
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializePersistentStorage,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTenantDate,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializePush,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppLifecycle,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeSafeArea,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeVersionCheck,
      multi: true,
    },
  ],
};
